import * as Y from 'yjs';
import { Awareness } from 'y-protocols/awareness';
import { nanoid } from 'nanoid';
import {
  META_DOC_KEY,
  META_FILES_MAP,
  META_SETTINGS_MAP,
  NOTE_TEXT,
  ResolveIndex,
  isCanvasPath,
  isMarkdownPath,
  noteDocKey,
  parseNote,
  shortestLinkText,
  titleFromPath,
  type VaultFileEntry,
  type VaultSettings,
} from '@rbv/shared';
import { SyncProvider } from './provider.js';
import { VaultPersistence } from './persistence.js';

export type FilesListener = (files: Map<string, VaultFileEntry>) => void;
export type NoteContentListener = (id: string, content: string) => void;

/**
 * The client's one entry point to vault state: owns the meta doc, all note
 * docs, persistence, and the sync provider. Full-vault replication: every
 * note/canvas doc is loaded from IDB and subscribed over the socket, so
 * search/graph/backlinks work offline.
 */
export class VaultClient {
  readonly persistence = new VaultPersistence();
  provider!: SyncProvider;
  meta!: Y.Doc;
  private noteDocs = new Map<string, Y.Doc>();
  private awarenesses = new Map<string, Awareness>();
  private filesListeners = new Set<FilesListener>();
  private contentListeners = new Set<NoteContentListener>();
  private started = false;
  /** Resolves when the meta doc has loaded from IDB (offline) — the UI can
   * render the vault immediately; server sync continues in background. */
  ready: Promise<void> = Promise.resolve();

  get files(): Y.Map<VaultFileEntry> {
    return this.meta.getMap<VaultFileEntry>(META_FILES_MAP);
  }

  get settingsMap(): Y.Map<unknown> {
    return this.meta.getMap(META_SETTINGS_MAP);
  }

  get settings(): VaultSettings {
    return Object.fromEntries(this.settingsMap.entries()) as VaultSettings;
  }

  setSetting<K extends keyof VaultSettings>(key: K, value: VaultSettings[K]) {
    if (value === undefined) this.settingsMap.delete(key as string);
    else this.settingsMap.set(key as string, value);
  }

  async start(): Promise<void> {
    if (this.started) return;
    this.started = true;
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    this.provider = new SyncProvider(`${proto}://${location.host}/api/sync`);
    await this.persistence.open();
    this.meta = new Y.Doc({ guid: META_DOC_KEY });
    await this.persistence.bind(META_DOC_KEY, this.meta);
    this.provider.addDoc(META_DOC_KEY, this.meta);

    // Ensure a doc exists locally for every known note; watch for new ones.
    this.files.observe(() => {
      void this.ensureNoteDocs();
      this.emitFiles();
    });
    await this.ensureNoteDocs();
    this.provider.connect();
  }

  private async ensureNoteDocs() {
    for (const [id, entry] of this.files.entries()) {
      if (entry.kind === 'file') continue;
      if (!this.noteDocs.has(id)) await this.loadNoteDoc(id);
    }
  }

  private loading = new Map<string, Promise<Y.Doc>>();

  private loadNoteDoc(id: string): Promise<Y.Doc> {
    const existing = this.noteDocs.get(id);
    if (existing) return Promise.resolve(existing);
    const inFlight = this.loading.get(id);
    if (inFlight) return inFlight;

    const doc = new Y.Doc({ guid: noteDocKey(id) });
    // Register the doc synchronously so a concurrent ytext()/loadNoteDoc sees
    // it; persistence.bind() attaches its update listener synchronously too,
    // so edits made during the async load are still persisted.
    this.noteDocs.set(id, doc);
    const text = doc.getText(NOTE_TEXT);
    text.observe(() => {
      for (const cb of this.contentListeners) cb(id, text.toString());
    });
    const p = (async () => {
      await this.persistence.bind(noteDocKey(id), doc);
      this.provider.addDoc(noteDocKey(id), doc);
      for (const cb of this.contentListeners) cb(id, text.toString());
      this.loading.delete(id);
      return doc;
    })();
    this.loading.set(id, p);
    return p;
  }

  // -- subscriptions ---------------------------------------------------------

  onFiles(cb: FilesListener): () => void {
    this.filesListeners.add(cb);
    if (this.meta) cb(this.filesSnapshot());
    return () => this.filesListeners.delete(cb);
  }

  /** Fires with full note content whenever any note doc changes (worker feed). */
  onNoteContent(cb: NoteContentListener): () => void {
    this.contentListeners.add(cb);
    return () => this.contentListeners.delete(cb);
  }

  filesSnapshot(): Map<string, VaultFileEntry> {
    return new Map(this.files.entries());
  }

  private emitFiles() {
    const snap = this.filesSnapshot();
    for (const cb of this.filesListeners) cb(snap);
  }

  // -- doc access ------------------------------------------------------------

  getEntry(id: string): VaultFileEntry | undefined {
    return this.files.get(id);
  }

  findByPath(path: string): string | null {
    for (const [id, e] of this.files.entries()) {
      if (!e.deleted && e.path === path) return id;
    }
    return null;
  }

  noteDoc(id: string): Y.Doc | null {
    return this.noteDocs.get(id) ?? null;
  }

  ytext(id: string): Y.Text | null {
    return this.noteDocs.get(id)?.getText(NOTE_TEXT) ?? null;
  }

  noteContent(id: string): string {
    return this.ytext(id)?.toString() ?? '';
  }

  awareness(id: string): Awareness {
    let a = this.awarenesses.get(id);
    if (!a) {
      const doc = this.noteDocs.get(id);
      if (!doc) throw new Error(`no doc for note ${id}`);
      a = new Awareness(doc);
      a.setLocalState(null); // no presence until an editor sets it
      this.awarenesses.set(id, a);
      this.provider.addAwareness(noteDocKey(id), a);
    }
    return a;
  }

  /** Clear local presence for a note when its view closes, so we don't
   * broadcast a heartbeat (and appear "present") for every note ever opened.
   * The Awareness instance is kept for reuse but goes idle with null state. */
  releaseAwareness(id: string) {
    this.awarenesses.get(id)?.setLocalState(null);
  }

  // -- actions ---------------------------------------------------------------

  /** Create a note (or canvas) at `path`; returns its id. No-op if the path
   * is already taken (returns the existing id). */
  async createNote(path: string, content = ''): Promise<string> {
    const existing = this.findByPath(path);
    if (existing) return existing;
    const id = nanoid(12);
    const now = Date.now();
    const kind = isCanvasPath(path) ? 'canvas' : 'note';
    this.files.set(id, { path, kind, ctime: now, mtime: now });
    const doc = await this.loadNoteDoc(id);
    if (content) doc.getText(NOTE_TEXT).insert(0, content);
    return id;
  }

  /** Tombstone a file. The server moves the materialized file to .trash/. */
  deleteFile(id: string) {
    const e = this.files.get(id);
    if (!e || e.deleted) return;
    this.files.set(id, { ...e, deleted: Date.now(), mtime: Date.now() });
  }

  restoreFile(id: string) {
    const e = this.files.get(id);
    if (!e || !e.deleted) return;
    const { deleted: _drop, ...rest } = e;
    this.files.set(id, { ...rest, mtime: Date.now() });
  }

  touch(id: string) {
    const e = this.files.get(id);
    if (e) this.files.set(id, { ...e, mtime: Date.now() });
  }

  /**
   * Rename/move a file and rewrite inbound wikilinks across the vault.
   * Re-parses every note synchronously against a fresh ResolveIndex, so the
   * rewrite is exact even while the async index worker is behind.
   */
  renameFile(id: string, newPath: string) {
    const entry = this.files.get(id);
    if (!entry || entry.path === newPath) return;
    const oldPath = entry.path;

    // Build resolution state BEFORE the rename.
    const parses = new Map<string, ReturnType<typeof parseNote>>();
    const aliases = new Map<string, string[]>();
    for (const [nid, e] of this.files.entries()) {
      if (e.deleted || e.kind !== 'note') continue;
      const p = parseNote(this.noteContent(nid));
      parses.set(nid, p);
      if (p.aliases.length) aliases.set(nid, p.aliases);
    }
    const before = new ResolveIndex(this.files.entries(), aliases);

    this.files.set(id, { ...entry, path: newPath, mtime: Date.now() });

    // New canonical link text for the moved file.
    const allEntries = [...this.files.entries()]
      .filter(([, e]) => !e.deleted)
      .map(([, e]) => e);
    const newLinkText = entry.kind === 'file'
      ? newPath
      : shortestLinkText(newPath, allEntries);

    const buildWiki = (
      linkText: string,
      w: { heading?: string; blockId?: string; alias?: string; embed: boolean },
    ) => {
      let inner = linkText;
      if (w.heading) inner += `#${w.heading}`;
      if (w.blockId) inner += `#^${w.blockId}`;
      if (w.alias) inner += `|${w.alias}`;
      return `${w.embed ? '!' : ''}[[${inner}]]`;
    };
    const encodeHref = (path: string, frag: string) =>
      path.split('/').map(encodeURIComponent).join('/') + frag;
    const mdHrefTarget = (href: string) => {
      const hashIdx = href.search(/[#]/);
      const base = hashIdx === -1 ? href : href.slice(0, hashIdx);
      const frag = hashIdx === -1 ? '' : href.slice(hashIdx);
      let decoded = base;
      try {
        decoded = decodeURIComponent(base);
      } catch {
        /* leave as-is */
      }
      return { target: decoded, frag };
    };

    for (const [nid, p] of parses.entries()) {
      const yt = this.ytext(nid);
      const src = this.getEntry(nid);
      if (!yt || !src) continue;
      // For the moved note, resolution used its OLD path.
      const fromPath = nid === id ? oldPath : src.path;
      const edits: Array<{ start: number; end: number; text: string }> = [];

      for (const w of p.wikilinks) {
        if (!w.target) continue;
        const resolved = before.resolve(w.target, fromPath);
        if (resolved === id) {
          edits.push({ start: w.start, end: w.end, text: buildWiki(newLinkText, w) });
        } else if (nid === id && resolved && w.target.includes('/')) {
          // The moved note's own relative path link — re-point it to the
          // target's folder-independent shortest form so it still resolves.
          const tp = before.pathFor(resolved);
          if (tp) edits.push({ start: w.start, end: w.end, text: buildWiki(shortestLinkText(tp, allEntries), w) });
        }
      }

      for (const l of p.mdLinks) {
        if (!l.internal || !l.href) continue;
        const { target, frag } = mdHrefTarget(l.href);
        const resolved = before.resolve(target, fromPath);
        if (resolved === id) {
          edits.push({
            start: l.start,
            end: l.end,
            text: `${l.embed ? '!' : ''}[${l.text}](${encodeHref(newPath, frag)})`,
          });
        } else if (nid === id && resolved && target.includes('/')) {
          const tp = before.pathFor(resolved);
          if (tp) {
            edits.push({
              start: l.start,
              end: l.end,
              text: `${l.embed ? '!' : ''}[${l.text}](${encodeHref(tp, frag)})`,
            });
          }
        }
      }

      if (edits.length === 0) continue;
      // Apply from the end so earlier offsets stay valid.
      edits.sort((a, b) => b.start - a.start);
      this.noteDocs.get(nid)!.transact(() => {
        for (const e of edits) {
          yt.delete(e.start, e.end - e.start);
          yt.insert(e.start, e.text);
        }
      });
    }
  }

  /** Rename a folder: move every live file under oldPrefix to newPrefix. */
  renameFolder(oldPrefix: string, newPrefix: string) {
    const moves: Array<[string, string]> = [];
    for (const [nid, e] of this.files.entries()) {
      if (e.deleted) continue;
      if (e.path === oldPrefix || e.path.startsWith(oldPrefix + '/')) {
        moves.push([nid, newPrefix + e.path.slice(oldPrefix.length)]);
      }
    }
    for (const [nid, np] of moves) this.renameFile(nid, np);
  }

  titleOf(id: string): string {
    const e = this.files.get(id);
    return e ? titleFromPath(e.path) : '';
  }
}

/** App-wide singleton. */
export const vault = new VaultClient();
